import { type EligibilityContext } from '@heidi/runtime/shell';
import { type SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useWorkspaceId } from '@heidi/workspace/data';
import { useRef } from 'react';

import type { UserBillingInfo } from '../../../contracts/types/commerce.types';
import type { CreditAllowanceResponse } from '../../../contracts/types/credit-allowance.types';
import { getCreditAllowanceNudgeVariant } from '../../../data/query/credit-allowance.utils';
import {
  getCreditAllowanceThresholdBillingPlanKey,
  isCreditAllowanceThresholdBillingPlanResolved,
} from '../../settings/get-credit-allowance-threshold-billing-plan-key';
import { isCreditUsageSurfaceEnabled } from '../../settings/is-credit-usage-surface-enabled';
import { isCreditUsageSurfaceTier } from '../../settings/is-credit-usage-surface-tier';
import { isMaxBillingSeat } from '../../settings/is-max-billing-seat';
import {
  type CreditAllowanceThresholdConsumedState,
  isThresholdConsumedInRecord,
} from './creditAllowanceThresholdStorage';

export type SideNavCreditUsageThresholdPopoverLaunchGate = Pick<
  EligibilityContext,
  'isHeidi2OptInEnabled' | 'isUserManaged'
> & {
  newUserTier: SubscriptionTier | null | undefined;
  /** False while a workspace switch is hard-reloading the app. */
  isWorkspaceIdSettled: boolean;
  /** The app build check, read where `isAppleCompliantBuild()` is read today. */
  isAppleCompliantBuild: () => boolean;
};

type CreditUsageLaunchBillingInfo =
  | (NonNullable<Parameters<typeof isCreditAllowanceThresholdBillingPlanResolved>[0]> &
      Partial<Pick<UserBillingInfo, 'heidi_version'>>)
  | null
  | undefined;

/**
 * A workspace switch writes the new id and then hard-reloads (`workspace-top-menu`),
 * so the id changing mid-session means this document is on its way out — the flag
 * resync for the new workspace must not flash the popover before the reload lands.
 * `null -> id` is boot hydration, not a switch.
 */
export function useWorkspaceIdSettledForCreditUsagePopoverLaunch(): boolean {
  const workspaceId = useWorkspaceId();
  const hydratedWorkspaceIdRef = useRef<string | null>(null);

  if (workspaceId && !hydratedWorkspaceIdRef.current) {
    hydratedWorkspaceIdRef.current = workspaceId;
  }

  return !workspaceId || hydratedWorkspaceIdRef.current === workspaceId;
}

/** Pure launch gate — allowance/billing come from GlobalModalsWrapper hooks, not cache peek. */
export function computeShouldQueueSideNavCreditUsageThresholdPopover(
  gate: SideNavCreditUsageThresholdPopoverLaunchGate,
  billingInfo: CreditUsageLaunchBillingInfo,
  allowance: CreditAllowanceResponse | null | undefined,
  consumedThresholds: CreditAllowanceThresholdConsumedState = {}
): boolean {
  if (!gate.isWorkspaceIdSettled) {
    return false;
  }
  // T3 R2 — framework flag is default OFF, so the queue cannot suppress this.
  if (gate.isUserManaged) {
    return false;
  }
  if (
    !isCreditUsageSurfaceEnabled({
      isHeidi2OptInEnabled: gate.isHeidi2OptInEnabled,
      heidiVersion: billingInfo?.heidi_version,
    })
  ) {
    return false;
  }
  if (gate.isAppleCompliantBuild()) {
    return false;
  }
  if (!isCreditUsageSurfaceTier(gate.newUserTier)) {
    return false;
  }

  if (!isCreditAllowanceThresholdBillingPlanResolved(billingInfo)) {
    return false;
  }

  // A Max seat (Clinician or Practice) has overage, not a cap, so no nudge.
  if (isMaxBillingSeat(billingInfo)) {
    return false;
  }

  if (allowance == null) {
    return false;
  }

  const threshold = getCreditAllowanceNudgeVariant(allowance);
  if (threshold == null) {
    return false;
  }

  const billingPlanKey = getCreditAllowanceThresholdBillingPlanKey(billingInfo);
  return !isThresholdConsumedInRecord(
    consumedThresholds,
    billingPlanKey,
    allowance.cycle_end,
    threshold
  );
}
